import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, TrendingUp, AlertCircle, CheckCircle, Search, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import api from '../../api';

export default function FeesPage() {
  const [summary, setSummary] = useState({});
  const [structures, setStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [generatingInvoices, setGeneratingInvoices] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sumRes, strRes, invRes] = await Promise.all([
        api.get('/fees/summary'),
        api.get('/fees/structures'),
        api.get(`/fees/invoices?search=${search}&status=${statusFilter === 'all' ? '' : statusFilter}`),
      ]);
      setSummary(sumRes.data || {});
      setStructures(strRes.data || []);
      setInvoices(invRes.data || []);
    } catch { toast.error('Failed to load fee data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, statusFilter]);

  const formatNaira = (amount) => `₦${(amount || 0).toLocaleString()}`;

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">Fees & Payments</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage school fees, invoices, and payment collection</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Invoiced', value: formatNaira(summary.total_invoiced), icon: FileText, color: 'from-[#516EFA] to-[#7B8EFC]' },
          { label: 'Collected', value: formatNaira(summary.total_collected), icon: CheckCircle, color: 'from-[#14C8C4] to-[#1BE6E2]' },
          { label: 'Outstanding', value: formatNaira(summary.outstanding), icon: AlertCircle, color: 'from-amber-500 to-amber-400' },
          { label: 'Paid', value: `${summary.paid_count || 0} / ${(summary.paid_count || 0) + (summary.unpaid_count || 0)}`, icon: TrendingUp, color: 'from-emerald-500 to-emerald-400' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <p className="font-outfit text-xl font-bold text-slate-900">{s.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="invoices">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="invoices" className="rounded-lg text-sm">Invoices</TabsTrigger>
            <TabsTrigger value="structures" className="rounded-lg text-sm">Fee Structures</TabsTrigger>
          </TabsList>
          <Button onClick={() => setShowStructureModal(true)} className="bg-[#516EFA] hover:bg-[#4058e8] text-white self-start">
            <Plus className="w-4 h-4 mr-2" /> New Fee Structure
          </Button>
        </div>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by student name or ID..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-10"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    {['Student', 'Class', 'Fee Type', 'Amount', 'Paid', 'Balance', 'Status', ''].map(h => (
                      <th key={h} className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? [...Array(5)].map((_, i) => (
                    <tr key={i}>{[...Array(8)].map((_, j) => <td key={j} className="px-6 py-4"><div className="h-4 bg-slate-200 rounded animate-pulse" /></td>)}</tr>
                  )) : invoices.length === 0 ? (
                    <tr><td colSpan={8}>
                      <div className="py-12 text-center">
                        <DollarSign className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-slate-500 text-sm">No invoices found</p>
                      </div>
                    </td></tr>
                  ) : invoices.map((inv, i) => (
                    <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-slate-800">{inv.student_name}</p>
                        <p className="text-xs text-slate-500">{inv.admission_number}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{inv.class_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{inv.fee_name}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-800">{formatNaira(inv.amount)}</td>
                      <td className="px-6 py-4 text-sm text-emerald-600">{formatNaira(inv.paid_amount)}</td>
                      <td className="px-6 py-4 text-sm text-red-500 font-medium">{formatNaira(inv.balance)}</td>
                      <td className="px-6 py-4">
                        <Badge className={`text-xs ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'partial' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {inv.status !== 'paid' && (
                          <Button size="sm" variant="outline" onClick={() => setShowPaymentModal(inv)} className="text-xs h-7 border-[#516EFA] text-[#516EFA]">
                            Record Payment
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Structures Tab */}
        <TabsContent value="structures">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-40 bg-slate-200 rounded-2xl animate-pulse" />) :
              structures.length === 0 ? (
                <div className="col-span-full bg-white rounded-2xl border border-slate-100 py-16 text-center">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium text-slate-600">No fee structures yet</p>
                  <Button size="sm" className="mt-4 bg-[#516EFA] text-white" onClick={() => setShowStructureModal(true)}>Create Fee Structure</Button>
                </div>
              ) : structures.map((s, i) => (
                <div key={s.id} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                    </div>
                    <Badge className="bg-[#eef1ff] text-[#516EFA] text-xs capitalize">{s.fee_type}</Badge>
                  </div>
                  <p className="font-semibold text-slate-800">{s.name}</p>
                  <p className="text-2xl font-outfit font-bold text-slate-900 mt-2">{formatNaira(s.amount)}</p>
                  {s.class_name && <p className="text-xs text-slate-500 mt-1">Class: {s.class_name}</p>}
                  {s.academic_year_name && <p className="text-xs text-slate-400">{s.academic_year_name}{s.term_name ? ` · ${s.term_name}` : ''}</p>}
                  {s.due_date && <p className="text-xs text-slate-400 mt-1">Due: {s.due_date}</p>}
                  <button
                    onClick={() => setGeneratingInvoices(s)}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors"
                    data-testid={`generate-invoices-btn-${s.id}`}
                  >
                    <FileText className="w-3.5 h-3.5" /> Generate Invoices
                  </button>
                </div>
              ))
            }
          </div>
        </TabsContent>
      </Tabs>

      {showStructureModal && <FeeStructureModal onClose={() => setShowStructureModal(false)} onSuccess={() => { setShowStructureModal(false); load(); }} />}
      {showPaymentModal && <RecordPaymentModal invoice={showPaymentModal} onClose={() => setShowPaymentModal(null)} onSuccess={() => { setShowPaymentModal(null); load(); }} />}
      {generatingInvoices && <GenerateInvoicesModal structure={generatingInvoices} onClose={() => setGeneratingInvoices(null)} onSuccess={() => { setGeneratingInvoices(null); load(); }} />}
    </div>
  );
}

function FeeStructureModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [years, setYears] = useState([]);
  const [allTerms, setAllTerms] = useState([]);
  const [form, setForm] = useState({
    name: '', amount: '', fee_type: 'tuition', due_date: '',
    class_id: '', class_name: '',
    academic_year_id: '', academic_year_name: '',
    term_id: '', term_name: '',
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [clsRes, sessionRes, yearsRes] = await Promise.all([
          api.get('/academics/classes'),
          api.get('/academics/current-session'),
          api.get('/academics/years'),
        ]);
        setClasses(clsRes.data || []);
        setYears(yearsRes.data || []);
        // Default to current session
        const { year, term, terms_for_year } = sessionRes.data || {};
        setAllTerms(terms_for_year || []);
        setForm(p => ({
          ...p,
          academic_year_id: year?.id || '',
          academic_year_name: year?.name || '',
          term_id: term?.id || '',
          term_name: term?.name || '',
        }));
      } catch { /* ignore */ }
    };
    loadMeta();
  }, []);

  // When academic year changes, update terms
  const handleYearChange = async (yearId) => {
    const year = years.find(y => y.id === yearId);
    update('academic_year_id', yearId);
    update('academic_year_name', year?.name || '');
    update('term_id', '');
    update('term_name', '');
    if (yearId) {
      try {
        const res = await api.get('/academics/terms');
        setAllTerms((res.data || []).filter(t => t.academic_year_id === yearId));
      } catch { /* ignore */ }
    }
  };

  const handleClassChange = (classId) => {
    const cls = classes.find(c => c.id === classId);
    update('class_id', classId);
    update('class_name', cls?.name || '');
  };

  const handleTermChange = (termId) => {
    const term = allTerms.find(t => t.id === termId);
    update('term_id', termId);
    update('term_name', term?.name || '');
  };

  const handleSubmit = async () => {
    if (!form.name || !form.amount) { toast.error('Name and amount are required'); return; }
    if (!form.class_id) { toast.error('Please select a class'); return; }
    setLoading(true);
    try {
      await api.post('/fees/structures', { ...form, amount: parseFloat(form.amount) });
      toast.success('Fee structure created');
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const termOptions = form.academic_year_id
    ? allTerms.filter(t => t.academic_year_id === form.academic_year_id || !t.academic_year_id)
    : allTerms;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-outfit">Create Fee Structure</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Fee Name */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Fee Name *</Label>
            <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="First Term Tuition" className="h-10" data-testid="fee-name-input" />
          </div>

          {/* Class */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Class * <span className="text-slate-400 font-normal text-xs">(fees are set per class)</span></Label>
            <Select value={form.class_id} onValueChange={handleClassChange}>
              <SelectTrigger className="h-10" data-testid="fee-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>
                {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Academic Session + Term */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Academic Session</Label>
              <Select value={form.academic_year_id} onValueChange={handleYearChange}>
                <SelectTrigger className="h-10" data-testid="fee-year-select"><SelectValue placeholder="Select session" /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y.id} value={y.id}>{y.name}{y.is_current ? ' (current)' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Term</Label>
              <Select value={form.term_id} onValueChange={handleTermChange} disabled={!form.academic_year_id}>
                <SelectTrigger className="h-10" data-testid="fee-term-select"><SelectValue placeholder="Select term" /></SelectTrigger>
                <SelectContent>
                  {termOptions.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Amount + Fee Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Amount (₦) *</Label>
              <Input type="number" value={form.amount} onChange={e => update('amount', e.target.value)} placeholder="150000" className="h-10" data-testid="fee-amount-input" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Fee Type</Label>
              <Select value={form.fee_type} onValueChange={v => update('fee_type', v)}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{['tuition','exam','registration','library','transport','uniform','boarding','other'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Due Date</Label>
            <Input type="date" value={form.due_date} onChange={e => update('due_date', e.target.value)} className="h-10" />
          </div>

          <div className="flex gap-3 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#516EFA] text-white" data-testid="create-fee-structure-btn">
              {loading ? 'Creating...' : 'Create Fee Structure'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentModal({ invoice, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ invoice_id: invoice.id, amount: invoice.balance, payment_method: 'cash', reference: '', notes: '' });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.amount || form.amount <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      await api.post('/fees/payments', { ...form, amount: parseFloat(form.amount) });
      toast.success('Payment recorded successfully');
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-outfit">Record Payment</DialogTitle></DialogHeader>
        <div className="p-3 bg-slate-50 rounded-xl text-sm mb-4">
          <p className="font-semibold text-slate-800">{invoice.student_name}</p>
          <p className="text-slate-500 text-xs">{invoice.fee_name} • Balance: ₦{invoice.balance?.toLocaleString()}</p>
        </div>
        <div className="space-y-4">
          <div><Label className="text-sm font-medium mb-1.5 block">Amount (₦)</Label><Input type="number" value={form.amount} onChange={e => update('amount', e.target.value)} className="h-10" /></div>
          <div><Label className="text-sm font-medium mb-1.5 block">Payment Method</Label>
            <Select value={form.payment_method} onValueChange={v => update('payment_method', v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="online">Online</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-sm font-medium mb-1.5 block">Reference (Optional)</Label><Input value={form.reference} onChange={e => update('reference', e.target.value)} placeholder="Transaction reference..." className="h-10" /></div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#516EFA] text-white">{loading ? 'Saving...' : 'Record Payment'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function GenerateInvoicesModal({ structure, onClose, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get(`/fees/structures/${structure.id}/invoice-preview`)
      .then(res => setPreview(res.data))
      .catch(() => setPreview({ total_students: 0, new_invoices: 0, existing_invoices: 0, class_names: [] }))
      .finally(() => setLoading(false));
  }, [structure.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/fees/structures/${structure.id}/generate-invoices`);
      const { created, skipped, message } = res;
      toast.success(message || `${created} invoice(s) generated`);
      onSuccess();
    } catch (err) {
      toast.error(err?.detail || 'Failed to generate invoices');
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-outfit">Generate Invoices</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="bg-indigo-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-800">{structure.name}</p>
            <p className="text-xs text-slate-500">
              {structure.class_name && <span>Class: <strong>{structure.class_name}</strong> · </span>}
              {structure.academic_year_name && <span>{structure.academic_year_name}</span>}
              {structure.term_name && <span> · {structure.term_name}</span>}
            </p>
            <p className="text-2xl font-bold text-indigo-700">₦{Number(structure.amount || 0).toLocaleString()}</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : preview && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xl font-bold text-slate-900">{preview.total_students}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Total Students</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3">
                <p className="text-xl font-bold text-emerald-700">{preview.new_invoices}</p>
                <p className="text-[11px] text-emerald-600 mt-0.5">New Invoices</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-xl font-bold text-amber-700">{preview.existing_invoices}</p>
                <p className="text-[11px] text-amber-600 mt-0.5">Already Exist</p>
              </div>
            </div>
          )}

          {preview?.new_invoices === 0 && !loading && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              All students in this class already have invoices for this fee structure.
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || loading || (preview?.new_invoices === 0)}
              className="flex-1 bg-[#516EFA] text-white"
              data-testid="confirm-generate-invoices-btn"
            >
              {generating ? 'Generating...' : `Generate ${preview?.new_invoices || 0} Invoice${preview?.new_invoices !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
